import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Form, Formik } from 'formik';
import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

import { useLoader } from '../../provider/LoaderProvider';
import FormFields from './FormFields';
import { DeleteDialogUI, FormButtonPanelUI } from '../Interface';
import useUsers from '../../context/UsersContext';
import dayjs from 'dayjs';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import TaskListReportTabs from './TaskListReportTabs';
import taskListService from '../../service/taskList.service';
import useMenus from '../../context/MenuContext';
import useParam from '../../context/ParamContext';
import useConfig from '../../context/ConfigContext';
import { QuantityUnitGroup } from '../../config/quantityUnit';

export const TaskListReportPage = ({ data = {}, handleClose = () => {}, setTaskChanged = () => {} }) => {
  const { getUser, user } = useUsers();
  const { menus, getMenus, getMenuSubMenuId } = useMenus();
  const { getParam, params } = useParam();
  const { config, getConfig } = useConfig();
  const { showLoader, hideLoader } = useLoader();
  const [initValues, setInitValues] = useState({});
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    project: [],
    status: [],
    qtyUnit: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [simpleTaskSubmenu, setSimpleTaskSubmenu] = useState(null);
  const [reports, setReports] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  const def = {
    projectId: null,
    taskListId: null,
    date: dayjs(),
    startTime: dayjs('07:00', 'HH:mm'),
    endTime: dayjs('16:00', 'HH:mm'),
    attachment: [],
    description: '',
    timeSpent: null,
    quantityUnitId: null,
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
    setInitValues(def);
    getMenus();
    getParam();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('simpleTask');
    setSimpleTaskSubmenu(submenuId);
  }, [menus]);

  useEffect(() => {
    if (simpleTaskSubmenu) initDefaultValues();
  }, [simpleTaskSubmenu]);

  useEffect(() => {
    if (Object.keys(data).length > 0 && !isLoading) {
      setInitValues((prev) => ({
        ...prev,
        ...def,
        ...data,
      }));
      if (data.taskListId) getTaskListReports(data.taskListId);
    }
  }, [data, isLoading]);

  useEffect(() => {
    if (Object.keys(config).length > 0 && !isLoading) {
      setInitValues((prev) => ({
        ...prev,
        startTime: dayjs(config.defTaskListReportFromTime).isValid()
          ? dayjs.utc(config.defTaskListReportFromTime)
          : prev.startTime,
        endTime: dayjs(config.defTaskListReportToTime).isValid()
          ? dayjs.utc(config.defTaskListReportToTime)
          : prev.endTime,
        timeSpent: config.defTaskListReportTimeSpent ?? prev.timeSpent,
        quantityUnitId: config.defTaskListReportQtyUnit ?? prev.quantityUnitId,
      }));
    }
  }, [config, isLoading]);

  const validationSchema = Yup.object().shape({
    projectId: Yup.string().required(),
    date: Yup.string().required(),
    startTime: Yup.string().required(),
    endTime: Yup.string().required(),
    description: Yup.string().nullable(),
    taskListId: Yup.string().required(),
    statusId: Yup.string().required(),
    preparedness: Yup.string().nullable(),
    timeSpent:
      params.TASKLIST_REPORT_SHOW_TIMESPENT_FIELD === 'Y'
        ? Yup.number().positive().required()
        : Yup.number().positive().nullable(),
    quantityUnitId: Yup.string().nullable(),
  });

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .statusToForm(simpleTaskSubmenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .quantityUnitToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          status: resp[1],
          qtyUnit: resp[2],
        };
        setAutoCompleteData(aData);

        const filteredStatus = aData.status.statusItems.filter((item) => item.onKanban);
        const filteredQtyUnit = aData?.qtyUnit?.filter((opQtyUnit) => opQtyUnit.group === QuantityUnitGroup.TIME);
        const filteredProject = aData.project.filter((p) => p.active);

        const acOptions = {
          project: generateAutocompleteItems(filteredProject, ['projectName', 'orderNumber'], 'projectId'),
          status: generateAutocompleteItems(filteredStatus, 'itemName', 'itemId'),
          qtyUnit: generateAutocompleteItems(filteredQtyUnit, 'code', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        //hideLoader();
        setIsLoading(false);
      });
  };

  const handleInsertObject = (values, { resetForm }) => {
    showLoader();

    const foundStatus = autoCompleteData.status.statusItems.find((status) => status.itemId === values.statusId);

    const startDate = dayjs(values.date)
      .hour(dayjs(values.startTime).hour())
      .minute(dayjs(values.startTime).minute())
      .second(0)
      .millisecond(0)
      .toISOString();

    const endDate = values.timeSpent
      ? dayjs(startDate).add(Number(values.timeSpent), 'hour').toISOString()
      : dayjs(values.date)
          .hour(dayjs(values.endTime).hour())
          .minute(dayjs(values.endTime).minute())
          .second(0)
          .millisecond(0)
          .toISOString();

    const insertObject = {
      ...values,
      createdBy: user.userId,
      startDate,
      endDate,
      preparedness: parseFloat(foundStatus?.preparedness ?? null),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    if (insertObject.attachment) {
      for (const file of insertObject.attachment) {
        formData.append('files', file);
      }
    }

    taskListService
      .createTaskListReport(formData)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        resetForm();
        handleClose();
        setTaskChanged(true);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getTaskListByLineId = async (projectId) => {
    await taskListService.getTasklistsByLineId(projectId).then((data) => {
      const options = generateAutocompleteItems(data, 'name', 'id');
      setAutoCompleteOptions((prev) => ({
        ...prev,
        taskLists: options,
      }));
      setAutoCompleteData((prev) => ({
        ...prev,
        taskLists: data,
      }));
    });
  };

  const getTaskListReports = async (taskListId) => {
    await taskListService.getTasklistReports(taskListId).then((data) => {
      setReports(data);
    });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog((prev) => ({
      ...prev,
      open: false,
      id: null,
    }));
  };

  const deleteTaskListReport = () => {
    const deleteData = {
      deletedBy: user.userId,
    };

    taskListService.deleteTaskListReport(deleteData, deleteDialog.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      closeDeleteDialog();
      getTaskListReports(data.taskListId);
    });
  };

  return (
    <div
      className={`${!Object.keys(data).length > 0 && 'pt-20 fixed top-0 left-0 overflow-auto'} bg-gray-100 h-full w-full`}
    >
      <div
        className={`${!Object.keys(data).length > 0 && 'py-5 px-5 md:w-[700px] w-[400px]  mx-auto'} h-auto bg-white rounded-lg`}
      >
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleInsertObject}
          enableReinitialize
        >
          {({ resetForm }) => (
            <Form>
              <FormFields
                autocompleteOptions={autocompleteOptions}
                getTaskListByLineId={getTaskListByLineId}
                autoCompleteData={autoCompleteData}
                viewField={Object.keys(data).length > 0}
                data={Object.keys(data).length > 0 ? data : {}}
              />
              <TaskListReportTabs
                data={Object.keys(data).length > 0}
                reports={reports}
                deleteTaskListReport={deleteTaskListReport}
                setDeleteDialog={setDeleteDialog}
              />
              <FormButtonPanelUI
                onCancelClick={() => {
                  if (!Object.keys(data).length > 0) {
                    resetForm();
                  } else {
                    handleClose();
                  }
                }}
              />
            </Form>
          )}
        </Formik>
      </div>
      <DeleteDialogUI
        handleRemoveElement={deleteTaskListReport}
        show={deleteDialog.open}
        onHide={() => closeDeleteDialog()}
      />
    </div>
  );
};
